import React, { useMemo, useCallback, useState } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';
import { usePaywallContext } from 'components/Paywall';
import { SECTION_KEYS } from 'components/Productions/Display/QuickView/constants';
import RentalBanner from 'components/Productions/Display/RentalBanner';

import { TP, DATE_FORMATS } from 'constants/index';

import ImpressionTracker from 'utils/components/impressionTracker';
import { createDate } from 'utils/date';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';
import { convertTimeFormat, getSlugH4TitleInfo } from 'utils/productions';
import { getContributors } from 'utils/productionSlug';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import Actions from './Actions';
import CondensedView from './CondensedView';
import Entirety from './Entirety';
import EntityRoles from './EntityRoles';
import FeatureMedia from './FeatureMedia';
import MediaList from './MediaList'; // eslint-disable-line import/no-cycle
import PerformanceDatesInfo from './PerformanceDatesInfo';
import PerformanceDatesVenues from './PerformanceDatesVenues';
import ProductionOtherTags from './ProductionOtherTags';
import classes from './ProductionPublicSlug.module.scss';
import ProductionPublicSlugDesktop from './ProductionPublicSlugDesktop';
import ProductionTags from './ProductionTags';
import Titles from './Titles';

const ConductorDirector = ({ title, primaryCrew, trackingData }) => {
  const { permissions } = usePageContext();
  const navigate = useNavigate();
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (!primaryCrew?.length) {
    return null;
  }

  const translatedKey = title === 'C:' ? t(`${TP}.m_CONDUCTOR`) : t(`${TP}.FN_DIRECTOR`);

  return (
    <>
      {primaryCrew.map((crew, index) => {
        const { entity } = crew || {};

        if (!entity?.id) {
          return null;
        }

        const primaryCrewLinkProps = navigate.getLinkProps({
          entityType: ENTITY_TYPES.PROFILE,
          entity,
          pro: permissions?.castingToolPermissions?.hasAccess,
          onlyLinkProps: true,
        });

        return (
          <div className={classes.cdBlock} key={`${entity?.id}-${index}`}>
            {index === 0 && (
              <Typography variant="h5" size={12} color="secondary">
                {translatedKey}:
              </Typography>
            )}
            <Typography variant="h6" size={12} className={classes.cdBlock__namesList}>
              <LinkButton
                isLink
                variant="text"
                {...primaryCrewLinkProps}
                preventDefault
                styles={{ root: classes.entityName }}
                trackingData={{
                  ...trackingData,
                  component: COMPONENTS.CONDUCTOR_DIRECTOR,
                  meta: {
                    id: entity?.id,
                  },
                }}
              >
                <EntityName entityType={ENTITY_TYPES.PROFILE} entity={entity} isRaw trackingData={trackingData} />
                {index !== primaryCrew.length - 1 && <span>,</span>}
              </LinkButton>
            </Typography>
          </div>
        );
      })}
    </>
  );
};

const ProductionPublicSlug = ({
  entity,
  entityType,
  data,
  upcoming = false,
  className,
  trackingData,
  openQuickPreview,
  isFeaturedCard = false,
  isCondensedView = false,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const track = useTracking();
  const navigate = useNavigate();
  const contributors = useMemo(() => getContributors({ data, entity, entityType }), [data, entity, entityType]);
  const [sectionKey, setSectionKey] = useState(null);
  const { setIsPaywallExpanded } = usePaywallContext();
  const isSmallScreen = useMediaQuery('(max-width: 1100px)');

  const isArchived = data?.isArchived;
  const isCancelled = data?.isCancelled;

  const { producers, directors, conductors, credits, entityRoles } = contributors;

  const restContributors = useMemo(() => credits.flatMap(credit => credit?.data), [credits]);

  const producerNames = useMemo(
    () =>
      producers?.data?.map((producer, index) => (
        <Typography
          key={`${producer?.entity?.id}-${index}`}
          weight="medium"
          strikethrough={isCancelled}
          size={12}
          blur={isArchived}
          onClick={e => {
            if (isArchived) {
              e.stopPropagation();
              e.preventDefault();
              setIsPaywallExpanded(true);
            }
          }}
        >
          <EntityName entity={producer?.entity} entityType={producer?.entityType} isRaw trackingData={trackingData} />
        </Typography>
      )),
    [producers?.data, isCancelled, isArchived, trackingData, setIsPaywallExpanded],
  );

  const handleOpenQuickPreview = useCallback(
    e => {
      if (e) {
        e.stopPropagation();
      }

      openQuickPreview(e, {
        productionId: data.id,
        scrollToSectionId: sectionKey,
      });

      track.click({
        ...trackingData,
        entityId: data.id,
        entityName: data.name,
        entityType: ENTITY_TYPES.PRODUCTION,
        meta: {
          ...(trackingData?.meta || {}),
          ...(data?.source_tag && { sourceTag: data?.source_tag }),
        },
      });
    },
    [openQuickPreview, track, trackingData, data.id, data.name, sectionKey],
  );

  const handlePerformanceClick = useCallback(
    e => {
      handleOpenQuickPreview(e);
    },
    [handleOpenQuickPreview],
  );

  const handleSectionKey = useCallback(
    (e, key) => {
      e.preventDefault();
      e.stopPropagation();
      setSectionKey(key);
      handleOpenQuickPreview(e);
    },
    [handleOpenQuickPreview],
  );

  const availableLanguages = data?.languages?.map(language => language?.name).join(', ');
  const availableSurtitles = data?.surtitles?.map(language => language?.name).join(', ');
  const timeDuration = useMemo(() => convertTimeFormat(data?.duration), [data?.duration]);

  const slugSeoH4 = useMemo(() => {
    const { productionName, creator, producerName, directedBy, conductedBy, yearTitleText, venue } =
      getSlugH4TitleInfo({
        production: data,
        entity,
        entityType,
      }) || {};

    const upcomingDate = data?.upcomingDates ? data?.dates?.[0]?.date : data?.performances?.[0]?.startDate;
    const date = createDate(upcomingDate).format(DATE_FORMATS.DAY_MONTH_YEAR);

    const details = [
      directedBy ? t(`${TP}.FN_DIRECTED_BY`, { director: directedBy }) : '',
      conductedBy ? t(`${TP}.FN_CONDUCTED_BY`, { conductor: conductedBy }) : '',
    ]
      .filter(Boolean)
      .join(' ');

    const venueText = venue ? `${t(`${TP}.AT`)} ${venue}` : '';
    const dateText = date ? `${t(`${TP}.FN_ON`)} ${date}` : '';

    return `${productionName} (from ${yearTitleText}), ${creator || ''} ${
      producerName || ''
    } ${details} ${venueText} ${dateText}`.trim();
  }, []);

  const getLinkProps = useCallback(
    ({ entityData, entityTypeInfo }) =>
      navigate?.getLinkProps({
        entityType: entityTypeInfo,
        entity: entityData,
      }),
    [navigate],
  );

  const linkProps = useMemo(
    () =>
      navigate.getLinkProps({
        entityType: ENTITY_TYPES.PRODUCTION,
        entity: data,
      }),
    [navigate, data, getLinkProps],
  );

  if (isCondensedView) {
    return (
      <CondensedView
        data={data}
        producerNames={producerNames}
        handlePerformanceClick={handlePerformanceClick}
        conductor={conductors?.data?.[0]?.entity}
        director={directors?.data?.[0]?.entity}
        trackingData={trackingData}
        slugSeoH4={slugSeoH4}
      />
    );
  }

  if (!isSmallScreen && !isFeaturedCard) {
    return (
      <ProductionPublicSlugDesktop
        data={data}
        producerNames={producerNames}
        contributors={contributors}
        linkProps={linkProps}
        handlePerformanceClick={handlePerformanceClick}
        trackingData={trackingData}
        entity={entity}
        entityType={entityType}
        upcoming={upcoming}
        slugSeoH4={slugSeoH4}
        setSectionKey={setSectionKey}
      />
    );
  }

  return (
    <ImpressionTracker
      as="li"
      data={{
        ...trackingData,
        entityId: data.id,
        entityName: data.name,
        entityType: ENTITY_TYPES.PRODUCTION,
        meta: {
          ...(trackingData?.meta || {}),
          ...(data?.source_tag && { sourceTag: data?.source_tag }),
        },
      }}
      className={classnames(classes.slugItem, 'h-event')}
    >
      <article className={classnames(classes.root, className)} onClick={handlePerformanceClick}>
        <div
          className={classnames(classes.productionPublicSlug, {
            [classes.productionPublicSlugFeatured]: !!isFeaturedCard,
          })}
        >
          {isFeaturedCard ? (
            <FeatureMedia
              data={data}
              producer={producers?.data?.[0]?.entity}
              styles={{ featureMedia: classes.productionPublicSlug__featureMedia }}
            />
          ) : (
            <div className={classes.productionPublicSlug__media}>
              <div className={classes.media}>
                <MediaList production={data} hidePoster />
              </div>
            </div>
          )}
          <div className={classes.productionPublicSlug__genreDatesActions}>
            <div
              className={classnames(classes.productionPublicSlug__genreDatesActions__left, {
                [classes.productionPublicSlug__genreDatesActions__leftFeaturedCard]: isFeaturedCard,
              })}
            >
              <div className={classes.genreWrapper}>
                <ProductionTags data={data} isFeaturedCard={isFeaturedCard} />
              </div>
              <div className={classes.datesWrapper}>
                <div className={classes.dates}>
                  <Entirety production={data} isFeaturedCard />
                </div>
                <div className={classes.totalDates}>
                  {data?.upcomingDates?.length > 0 && (
                    <Typography size={12} className={classes.datesRemaining} color="secondary" truncate>
                      ({data?.upcomingDates?.length} dates)
                    </Typography>
                  )}
                </div>
              </div>
            </div>
            <div className={classes.productionPublicSlug__genreDatesActions__right}>
              <div className={classes.actionsWrapper}>
                <Actions
                  data={data}
                  entity={entity}
                  entityType={entityType}
                  trackingData={{
                    ...trackingData,
                    meta: {
                      ...(trackingData?.meta || {}),
                      ...(data?.source_tag && { sourceTag: data?.source_tag }),
                    },
                  }}
                />
              </div>
            </div>
          </div>
          {data?.rentals?.length > 0 && (
            <div className={classes.productionPublicSlug__rentalBannerBlock}>
              <RentalBanner
                entity={data}
                styles={{
                  rentalBanner: classes.rentalBanner,
                }}
                isFeaturedCard
              />
            </div>
          )}
          <div className={classes.productionPublicSlug__titleTagsDurationLangSurtitle}>
            <div className={classes.prodTitle}>
              <div className={classes.visuallyHidden}>
                <h4 id="performance-details" aria-hidden="true">
                  {slugSeoH4}
                </h4>
              </div>
              <Titles
                data={data}
                isCancelled={isCancelled}
                trackingData={trackingData}
                isFeaturedPerformance={isFeaturedCard}
              />
            </div>
            <div className={classes.titleTagsDurationLangSurtitle}>
              <ProductionOtherTags production={data} />
              {timeDuration && (
                <Typography
                  size={12}
                  className={classes.duration}
                  color="secondary"
                  {...(isCancelled && { strikethrough: true })}
                >
                  {t(`${TP}.FN_PRODUCTION_DURATION_TITLE`)}: {timeDuration}
                </Typography>
              )}
              {availableLanguages?.length > 0 && (
                <Typography
                  size={12}
                  color="secondary"
                  {...(isCancelled && { strikethrough: true })}
                  className={classes.language}
                >
                  {t(`${TP}.FN_LANGUAGE_LABEL`)}: {availableLanguages}
                </Typography>
              )}
              {availableSurtitles?.length > 0 && (
                <Typography
                  size={12}
                  className={classes.surtitle}
                  color="secondary"
                  {...(isCancelled && { strikethrough: true })}
                  truncate
                >
                  {t(`${TP}.SURTITLE`)}: {availableSurtitles}
                </Typography>
              )}
            </div>
          </div>
          <div className={classes.productionPublicSlug__producerCrew}>
            <div className={classes.producer}>{producerNames}</div>
            {(conductors?.data?.length > 0 || directors?.data?.length > 0) && (
              <div
                className={classnames(classes.conductorDirector, {
                  [classes.conductorDirector__featuredCard]: isFeaturedCard,
                })}
              >
                <div className={classes.conductor}>
                  <ConductorDirector title="C:" primaryCrew={conductors?.data} trackingData={trackingData} />
                </div>
                <div className={classes.director}>
                  <ConductorDirector primaryCrew={directors?.data} trackingData={trackingData} />
                </div>
              </div>
            )}
          </div>
          <div
            className={classnames(classes.productionPublicSlug__restCastCrew, {
              [classes.productionPublicSlug__restCastCrew_featuredCard]: isFeaturedCard,
            })}
          >
            {entityRoles?.length > 0 && (
              <div className={classnames({ [classes.entityRolesFeaturedCard]: isFeaturedCard })}>
                <EntityRoles
                  entityRoles={entityRoles}
                  entity={entity}
                  entityType={entityType}
                  data={data}
                  trackingData={trackingData}
                />
              </div>
            )}
            {restContributors?.length > 0 && (
              <div className={classes.restContributors}>
                <Typography size={12} color="secondary" className={classes.restContributors__label}>
                  {t(`${TP}.FN_STARRING`)}:
                </Typography>
                <div className={classes.restContributors__list}>
                  {restContributors?.slice(0, 6).map((credit, index, array) => (
                    <React.Fragment key={`${credit?.entity?.id}-${index}`}>
                      <LinkButton
                        isLink={!isArchived}
                        variant="text"
                        styles={{
                          root: classnames(classes.restContributors__link, {
                            [classes.restContributors__linkArchived]: isArchived,
                          }),
                        }}
                        {...getLinkProps({ entityData: credit?.entity, entityTypeInfo: credit?.entityType })}
                        {...(isArchived && {
                          onClick: e => {
                            e.stopPropagation();
                            e.preventDefault();
                            setIsPaywallExpanded(true);
                          },
                        })}
                        trackingData={{
                          ...trackingData,
                          component: COMPONENTS.CAST_CREW,
                          meta: {
                            id: credit?.entity?.id,
                          },
                        }}
                      >
                        <EntityName
                          entity={credit?.entity}
                          entityType={credit?.entityType}
                          isRaw
                          trackingData={trackingData}
                        />
                      </LinkButton>
                      {index < array.length - 1 && ', '}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            <Typography
              size={11}
              className={classnames(classes.viewAllcontributorsAction, {
                [classes.viewAllcontributorsAction__featuredCard]: isFeaturedCard,
              })}
            >
              {t(`${TP}.VIEW_CAST_CREW_INS_ENSEMBLES`)}
            </Typography>
          </div>
          {!isFeaturedCard && (
            <div
              className={classnames(classes.productionPublicSlug__performanceDates, {
                [classes.productionPublicSlug__seasonPerformanceDates]: !upcoming,
              })}
            >
              {upcoming ? (
                <PerformanceDatesInfo
                  data={data}
                  linkProps={linkProps}
                  openQuickPreview={openQuickPreview}
                  onClick={handlePerformanceClick}
                />
              ) : (
                <PerformanceDatesVenues data={data} />
              )}
            </div>
          )}
          {isFeaturedCard && (
            <div className={classes.productionPublicSlug__mediaStats}>
              {(data?.stats?.reviews?.exists || data?.reviewCount > 0) && (
                <span className={classes.actionText} onClick={e => handleSectionKey(e, SECTION_KEYS.REVIEWS)}>
                  <SpriteIcon icon="reviews" size={12} />
                  <Typography size={12}>{t(`${TP}.m_REVIEWS`)}</Typography>
                </span>
              )}
              {(data?.stats?.media?.videos?.exists || data?.videoCount > 0) && (
                <span className={classes.actionText} onClick={e => handleSectionKey(e, SECTION_KEYS.VIDEOS)}>
                  <SpriteIcon icon="image" size={12} />
                  <Typography size={12}>{t(`${TP}.m_VIDEOS`)}</Typography>
                </span>
              )}
              {(data?.stats?.media?.images?.exists || data?.imageCount > 0) && (
                <span className={classes.actionText} onClick={e => handleSectionKey(e, SECTION_KEYS.PHOTOS)}>
                  <SpriteIcon icon="image" size={12} />
                  <Typography size={12}>{t(`${TP}.m_IMAGES`)}</Typography>
                </span>
              )}
            </div>
          )}
        </div>
      </article>
    </ImpressionTracker>
  );
};

export default ProductionPublicSlug;
